/*
 * WF Product Labels — native storefront renderer.
 *
 * Fetches the admin-managed ruleset from the Planners worker, evaluates each
 * product against its conditions, and
 * overlays styled pills on the product gallery image (product page) and on
 * product cards (collection / search / home).
 *
 * The config is owned in the Planners dashboard (Operations > Labels & Badges);
 * this file is intentionally dumb — it only matches + renders.
 *
 * Product context is provided by the theme via `data-wf-label-ctx` (JSON):
 *   { id:Number, tags:String[], type:String, available:Boolean,
 *     publishedAt:Number (epoch seconds, 0 when unknown) }
 * placed on:
 *   - the product-page gallery container  (data-wf-label-surface="product")
 *   - each product card                   (data-wf-label-surface="card")
 */
(function () {
  'use strict';

  var API_BASE = 'https://planners.dand3.com';
  var STORE_KEY = (window.WF_LD_STORE_KEY || (window.WF_LD_RUNTIME && window.WF_LD_RUNTIME.storeKey) || 'WF');
  var ATTR = 'data-wf-label-ctx';
  var DONE = 'data-wf-labels-done';
  var INSTANCE_CLASS = 'wf-pl';

  // --- page type -----------------------------------------------------------
  // Page types: product | collection | search | home | cart.
  function pageType() {
    var t = (window.Shopify && window.Shopify.designMode, document.body && document.body.getAttribute('data-template')) || '';
    // Eurus exposes the template via <body class="template-..."> as well.
    var cls = (document.body && document.body.className) || '';
    var path = location.pathname;
    if (/template-product\b/.test(cls) || /^\/products\//.test(path)) return 'product';
    if (/template-collection\b/.test(cls) || /^\/collections\/[^/]+$/.test(path)) return 'collection';
    if (/template-search\b/.test(cls) || /^\/search/.test(path)) return 'search';
    if (/template-cart\b/.test(cls) || /^\/cart/.test(path)) return 'cart';
    if (/template-index\b/.test(cls) || path === '/' ) return 'home';
    return 'collection'; // safe default for grid surfaces
  }

  // --- ruleset fetch (cached in sessionStorage for the visit) --------------
  function loadRuleset() {
    var CACHE_KEY = 'wf_pl_ruleset_' + STORE_KEY;
    try {
      var hit = sessionStorage.getItem(CACHE_KEY);
      if (hit) {
        var parsed = JSON.parse(hit);
        if (parsed && parsed.t && (Date.now() - parsed.t) < 60000) return Promise.resolve(parsed.labels || []);
      }
    } catch (e) { /* ignore */ }
    return fetch(API_BASE + '/api/wf/product-labels/ruleset?storeKey=' + encodeURIComponent(STORE_KEY), {
      credentials: 'omit',
      cache: 'no-store',
    })
      .then(function (r) { return r.ok ? r.json() : { labels: [] }; })
      .then(function (j) {
        var labels = (j && j.labels) || [];
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), labels: labels })); } catch (e) { /* ignore */ }
        return labels;
      })
      .catch(function () { return []; });
  }

  // --- condition matching --------------------------------------------------
  function hasAny(have, want) {
    for (var i = 0; i < want.length; i++) if (have.indexOf(want[i]) !== -1) return true;
    return false;
  }

  function matches(label, ctx, page) {
    if (label.pages && label.pages.indexOf(page) === -1) return false;
    var m = label.match || {};
    var id = Number(ctx.id);
    var tags = ctx.tags || [];

    // Technical cart-price carriers stay published so Shopify can add them,
    // but they are not merchandise and must never receive storefront badges.
    if (ctx.type === 'WF Add-on Fee' || hasAny(tags, ['pfs:hidden', 'wf_addon_fee_hidden'])) return false;

    // exclusions
    if (m.excludeProductIds && m.excludeProductIds.length && m.excludeProductIds.indexOf(id) !== -1) return false;
    if (m.excludeTags && m.excludeTags.length && hasAny(tags, m.excludeTags)) return false;

    // inventory filter
    if (m.inventory === 'in' && ctx.available === false) return false;
    if (m.inventory === 'out' && ctx.available !== false) return false;

    // A label must have a POSITIVE selector that matches. "in stock" alone is
    // a filter, not a selector — otherwise status labels (Sale) hit everything.
    var hasProductSel = m.productIds && m.productIds.length > 0;
    var hasTagSel = m.tags && m.tags.length > 0;
    var hasNewSel = m.newerThanDays > 0;
    // New-arrival auto-selector (BUG-108): product published within the last
    // N days. While enabled it owns inclusion, so legacy manual product/tag
    // selectors cannot keep an expired product labelled indefinitely.
    if (hasNewSel) {
      var pub = Number(ctx.publishedAt);
      var ageSeconds = Date.now() / 1000 - pub;
      return pub > 0 && ageSeconds >= 0 && ageSeconds < m.newerThanDays * 86400;
    }
    if (hasProductSel && m.productIds.indexOf(id) !== -1) return true;
    if (hasTagSel && hasAny(tags, m.tags)) return true;
    // Sold-out is the one auto-selector: an out-of-stock label with no list.
    if (!hasProductSel && !hasTagSel && !hasNewSel && m.inventory === 'out') return true;

    // NOTE v1: collection / price / on-sale conditions are not
    // evaluated client-side yet (WF's rules are productId-driven). Add later.
    return false;
  }

  // --- rendering -----------------------------------------------------------
  function pill(label) {
    var el = document.createElement('span');
    el.className = INSTANCE_CLASS + ' ' + INSTANCE_CLASS + '--' + (label.position || 'top-left');
    el.setAttribute('data-wf-pl-id', label.id);
    var s = label.style || {};
    el.style.setProperty('--wf-pl-color', s.color || '#2a341c');
    el.style.setProperty('--wf-pl-bg', s.transparent ? 'transparent' : (s.background || '#b2ab9a'));
    el.style.setProperty('--wf-pl-radius', s.radius || '5px');
    el.style.setProperty('--wf-pl-pad', s.padding || '4px 8px');
    el.style.setProperty('--wf-pl-fs', (s.fontSize || 13) + 'px');
    el.style.setProperty('--wf-pl-op', (s.opacity == null ? 90 : s.opacity) / 100);
    el.textContent = label.text || '';
    return el;
  }

  // Overlay targets carry [data-wf-label-target] (e.g. each gallery image
  // wrapper). When none exist the context element is itself the target
  // (product cards: the anchor is position-relative).
  function targetsFor(host) {
    var found = host.querySelectorAll('[data-wf-label-target]');
    return found.length ? Array.prototype.slice.call(found) : [host];
  }

  function overlayOnto(target, apply) {
    var layer = target.querySelector(':scope > .wf-pl-layer');
    if (!apply.length) { if (layer) layer.remove(); return; }
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'wf-pl-layer';
      var cs = window.getComputedStyle(target);
      if (cs.position === 'static') target.style.position = 'relative';
      target.appendChild(layer);
    }
    layer.innerHTML = '';
    apply.forEach(function (l) { layer.appendChild(pill(l)); });
  }

  // Badge-placement labels render in the info area (below the title), not on
  // the image. We create the row ourselves (no Eurus block edits): product
  // page → after the title block; card → after the card title.
  function makeBadgeRow(scope) {
    var row = document.createElement('div');
    row.setAttribute('data-wf-label-badges', scope || '');
    row.className = 'wf-pl-badges';
    return row;
  }

  function badgeContainerFor(host, surface) {
    if (surface === 'card') {
      // Scope to the whole card so we find/create exactly one row per card.
      var card = host.closest('.card-product') || host.parentElement;
      if (!card) return null;
      var existing = card.querySelector('[data-wf-label-badges]');
      if (existing) return existing;
      var cardTitle = card.querySelector('.x-card-title, [class*="card-product-title"]');
      var anchorEl = cardTitle ? (cardTitle.closest('.card__heading') || cardTitle) : null;
      if (anchorEl && anchorEl.parentNode) {
        var crow = makeBadgeRow('card');
        anchorEl.parentNode.insertBefore(crow, anchorEl.nextSibling);
        return crow;
      }
      return null;
    }
    // Product: own marker (not any card's row) + the *visible* title block.
    var found = document.querySelector('[data-wf-label-badges="product"]');
    if (found) return found;
    var h1 = document.querySelector('h1.product-title');
    if (!h1) return null;
    var titleBlock = h1.closest('.x-block-product-title') || h1.parentElement;
    if (!titleBlock || !titleBlock.parentNode) return null;
    var prow = makeBadgeRow('product');
    titleBlock.parentNode.insertBefore(prow, titleBlock.nextSibling);
    return prow;
  }

  function fillBadges(container, labels) {
    Array.prototype.slice.call(container.querySelectorAll('.wf-pl')).forEach(function (n) { n.remove(); });
    labels.forEach(function (l) { container.appendChild(pill(l)); });
  }

  function renderInto(host, labels, page) {
    var ctxRaw = host.getAttribute(ATTR);
    if (!ctxRaw) return;
    var ctx;
    try { ctx = JSON.parse(ctxRaw); } catch (e) { return; }
    var surface = host.getAttribute('data-wf-label-surface') || 'card';
    // Cards are a grid surface even on a product page — so product-only labels
    // (flower-type) stay on the main product, not the recommendation cards.
    var effPage = surface === 'product'
      ? 'product'
      : ((page === 'collection' || page === 'search' || page === 'home') ? page : 'collection');
    var apply = labels.filter(function (l) { return matches(l, ctx, effPage); });
    var imageLabels = apply.filter(function (l) { return l.position !== 'badge'; });
    // Below-title badges render ONLY on the product page. On cards we never
    // inject into the title — recommendation/related carousels clone their
    // slides continuously, which would stack the badge line after line.
    var badgeLabels = surface === 'product'
      ? apply.filter(function (l) { return l.position === 'badge'; })
      : [];

    targetsFor(host).forEach(function (target) { overlayOnto(target, imageLabels); });
    if (surface === 'product') {
      var bc = badgeContainerFor(host, surface);
      if (bc) fillBadges(bc, badgeLabels);
    }
    host.setAttribute(DONE, '1');
  }

  function renderAll(labels) {
    var page = pageType();
    var hosts = document.querySelectorAll('[' + ATTR + ']');
    for (var i = 0; i < hosts.length; i++) renderInto(hosts[i], labels, page);
  }

  // --- boot + targeted self-heal (splide/quick-view add product nodes) -----
  function boot() {
    // Content pages without product surfaces do not need the rules request or
    // a document observer. Product grids and featured products already expose
    // their hosts in the server-rendered HTML before this deferred script runs.
    if (!document.querySelector('[' + ATTR + ']')) return;

    loadRuleset().then(function (labels) {
      if (!labels.length) return;
      var obs = null;
      var pending = false;
      var queued = [];

      function queue(host) {
        if (host && queued.indexOf(host) === -1) queued.push(host);
      }

      function collectHosts(node) {
        if (!node || node.nodeType !== 1) return;
        if (node.matches('[' + ATTR + ']')) queue(node);
        if (node.closest) queue(node.closest('[' + ATTR + ']'));
        var nested = node.querySelectorAll('[' + ATTR + ']');
        for (var i = 0; i < nested.length; i++) queue(nested[i]);
      }

      // Disconnect while rendering so our own pill writes do not re-trigger us.
      function runQueued() {
        pending = false;
        if (obs) obs.disconnect();
        var hosts = queued.splice(0, queued.length);
        for (var i = 0; i < hosts.length; i++) {
          if (!hosts[i].isConnected) continue;
          try { renderInto(hosts[i], labels, pageType()); } catch (e) { /* ignore */ }
        }
        if (obs) obs.observe(document.body, { childList: true, subtree: true });
      }

      function schedule() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(runQueued);
      }

      renderAll(labels);
      // Render only hosts added or changed by sliders and quick-view injection.
      obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) collectHosts(added[j]);
        }
        if (queued.length) schedule();
      });
      obs.observe(document.body, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
